import QueryString from 'qs'
import { UseMutationOptions, UseQueryOptions, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from '~/common/react-query/queryClient'
import { ApprovalModule, ComponentType } from '~/api/approvals'
import { workOrderApi } from '~/api/work_order'
import fetch from '~/common/fetch'
import { sowDetailsApi } from '~/api/sow/details'
import { workOrderQuickActionsApi } from '~/work_order/actions/workOrderActions'
import { offerReleaseApi } from '~/work_order/offer_release/offer_release'
import { ApprovalSourceType } from '~/settings/approvals/types'

const BASE_APPROVAL_URL = '/approval-manager'

/**
 * API for loading approvers on an approval item that is being created, to be approved in the future.
 */
export const workflowApproversApi = {
  load: async (approvalType: string, data: any) =>
    fetch.post(`${BASE_APPROVAL_URL}/get-wf-approvals/`, { approval_type: approvalType, ...data })
}

export type WorkflowApprover = {
  all_must_approve?: boolean
  email: string | null
  employer_id: number
  level: number
  name: string
  optional: boolean
  user_id: number
  source_type?: ApprovalSourceType
}

export type WorkflowApproverResponse = {
  approvers: WorkflowApprover[]
  has_approvers: boolean
  rule_found: boolean
  skip: boolean
}
export type QuickActionApprovalType = 'quick_action'
export type OfferApprovalType = 'offer_initial_release' | 'offer_revision'
export type RevisionApprovalType = 'work_order_revision'
export type ReopenApprovalType = 'work_order_reopen'
export type EndApprovalType = 'work_order_end'
export type DefaultWorkflowApprovalType =
  | 'job_publish_approval'
  | 'job_change_approval'
  | 'offer_release_approval'
  | 'bill_rate_change_approval'
  | 'contract_reopen_approval'
  | 'contract_termination_approval'
  | 'worker_tracker_creation_approval'
  | 'worker_tracker_end_approval'
  | 'worker_tracker_change_approval'
  | 'sow_change_orders'
  | 'sow_acceptance'
  | 'sow_accept_v2'
  | 'sow_closure'
  | 'sow_reopen'
  | 'sow_work_order_end'

export type ApprovalType =
  | DefaultWorkflowApprovalType
  | OfferApprovalType
  | QuickActionApprovalType
  | RevisionApprovalType
  | ReopenApprovalType
  | EndApprovalType

export type WorkOrderActionApproverApiOptions = {
  workOrderId: number
  quickAction: string
  approvalType: QuickActionApprovalType
}
export type OfferApproverApiOptions = {
  workOrderId?: number
  jobApplicationId?: number
  approvalType: OfferApprovalType
}
export type WorkOrderRevisionApiOptions = {
  workOrderId?: number
  approvalType: RevisionApprovalType
}
export type WorkOrderReopenApiOptions = {
  workOrderId?: number
  approvalType: ReopenApprovalType
}
export type WorkOrderEndApiOptions = {
  workOrderId?: number
  approvalType: EndApprovalType
}
export type DefaultWorkflowApproverApiOptions = { approvalType: DefaultWorkflowApprovalType }
export type WorkflowApproverApiOptions =
  | OfferApproverApiOptions
  | WorkOrderActionApproverApiOptions
  | DefaultWorkflowApproverApiOptions
  | WorkOrderRevisionApiOptions
  | WorkOrderReopenApiOptions
  | WorkOrderEndApiOptions

const WORKFLOW_APPROVERS_QUERY_KEY = 'workflow-approvers'
export const useWorkflowApproversQuery = (
  data: any,
  apiOptions: WorkflowApproverApiOptions,
  options?: UseQueryOptions<WorkflowApproverResponse>
) => {
  let apiCall: () => Promise<WorkflowApproverResponse>
  switch (apiOptions.approvalType) {
    case 'offer_initial_release':
    case 'offer_revision':
      // With WOAA we will always have a workOrderId
      apiCall = () => offerReleaseApi.approversForWOOffer(apiOptions.workOrderId!, data)
      break
    case 'quick_action':
      apiCall = () =>
        workOrderQuickActionsApi.approversForQuickActions(apiOptions.workOrderId, apiOptions.quickAction, data)
      break
    case 'work_order_revision':
      apiCall = () => workOrderApi.getApproversForRevision(apiOptions.workOrderId, data)
      break
    case 'work_order_reopen':
      apiCall = () => workOrderApi.getApproversForReopen(apiOptions.workOrderId, data)
      break
    case 'work_order_end':
      apiCall = () => workOrderApi.getApproversForEnd(apiOptions.workOrderId, data)
      break
    case 'sow_accept_v2':
      apiCall = () => sowDetailsApi.getApprovals(data.sow_id)
      break
    default:
      apiCall = () => workflowApproversApi.load(apiOptions.approvalType, data)
  }

  return useQuery({ queryKey: [WORKFLOW_APPROVERS_QUERY_KEY, apiOptions, data], queryFn: apiCall, ...options })
}

/**
 * API for loading approval configurations (if optional approvers can be added)
 */
export type WorkflowApprovalConfiguration = {
  can_add_additional_approver: boolean
  workflow_approvers?: {
    add_extra_optional_approvers?: boolean
    allow_additional_approvers_on_same_levels?: boolean
    enable_diagnostics?: boolean
  }
}

const WORKFLOW_APPROVALS_CONFIG_QUERY_KEY = 'workflow-approvals-config'
export const useWorkflowApproversConfigQuery = (params: { businessUnitId?: number }) => {
  return useQuery({
    queryKey: [WORKFLOW_APPROVALS_CONFIG_QUERY_KEY],
    queryFn: (): Promise<WorkflowApprovalConfiguration> =>
      fetch.get(
        '/api/v2/workflow_approval_config/' + QueryString.stringify(params, { skipNulls: true, addQueryPrefix: true })
      )
  })
}

export type ApproveOptions = { updated_comments?: string | null; reason?: string | null }
type AddCommentParams = { workflow_instance: number; comment: string }
type EditCommentParams = { id: number; comment: string }

/**
 * API for loading approvers on a "ready to approve" approval item.
 *
 * It's a factory, so pass a particular module from above obj to get a module-specific api
 */
export const makeApproversApi = (module: ApprovalModule) => {
  return {
    workflow: {
      load: (component_id: number) => fetch.get(`/api/v2/${module}/?component_id=${component_id}`),
      approvers: (component_id: number): Promise<ApproversResponse> =>
        fetch.get(`/api/v2/${module}/?component_id=${component_id}`),
      all_approvers: (component_id: number): Promise<AllApproversResponse> =>
        fetch.get(`/api/v2/${module}/all_approvers/?component_id=${component_id}`),
      approve: (task_uuid: string, { updated_comments = null, reason = null }: ApproveOptions = {}) =>
        fetch.post(`/api/v2/${module}/${task_uuid}/approve/`, { updated_comments, reason }),
      bulk_approve: (component_ids: number[]) => fetch.post(`/api/v2/${module}/bulk_approve/`, { component_ids }),
      deny: (task_uuid: string, reason: string) => fetch.post(`/api/v2/${module}/${task_uuid}/reject/`, { reason }),
      bulk_deny: (component_ids: number[], reason: string) =>
        fetch.post(`/api/v2/${module}/bulk_reject/`, { component_ids, reason }),
      renotify_approvers: (task_uuid: string) => fetch.post(`/api/v2/${module}/${task_uuid}/renotify_approvers/`, {})
    }
  }
}

export const approverCommentsApi = {
  addComment: (apiParams: AddCommentParams): Promise<any> => fetch.post(`/api/v2/approval-comments/`, apiParams),
  editComment: (apiParams: EditCommentParams): Promise<any> =>
    fetch.patch(`/api/v2/approval-comments/${apiParams.id}/`, { comment: apiParams.comment }),
  deleteComment: (id: number): Promise<any> => fetch.delete(`/api/v2/approval-comments/${id}/`)
}

export enum TaskState {
  Maybe = 1,
  Likely = 2,
  Future = 4,
  Waiting = 8,
  Ready = 16,
  Completed = 32,
  Cancelled = 64
}

export type TaskData = {
  email?: string
  reason?: string
  user_id?: string
  namespace?: 'approval'
  approved?: boolean
  approver_email?: string
  approval_timestamp?: string
  [key: string]: any
}

export type TaskInstance = {
  uuid: string
  parent_uuid: string
  task_name: string
  state: TaskState
  state_text: keyof typeof TaskState
  user: number
  name: string
  subname: string
  component_type: ComponentType
  component_id: number
  reason: null | string
  task_data: TaskData | null
  can_approve: boolean
  can_override_approve: null | boolean
  tags: string[]
}
export type ApproverType = 'and' | 'or' | 'optional'
export type Approver = {
  step: string
  user_id: number
  email: string
  type: ApproverType
  task_instance: null | TaskInstance
  approver_name: string
  reviewed_by_user_id?: number
  reviewed_by_name?: string
}
export type ApproverStep = {
  is_active: boolean
  is_rejected: boolean
  and_approvers: Approver[]
  or_approvers: Approver[]
  optional_approvers: Approver[]
  approval_group_name?: string
}
// TODO: Finish comment typing
export type Comment = {
  // approval/rejection comments don't have id
  id?: number
  comment_user: string
  comment_user_id: number
  action: null | string
  comment: null | string
  created_at: string
  updated_at: string
}

export type ApproversResponse = {
  results: TaskInstance[]
}

export type AllApproversResponse = {
  workflows: WorkflowApprovers[]
  allApprovalsAreComplete: boolean
  workflowsHaveRejections: boolean
}

export type WorkflowApprovers = {
  workflow_id: number
  related_object?: WorkflowRelatedObject
  approvers: ApproverStep[]
  comments: Comment[]
}

export type WorkflowRelatedObject = {
  id: number
  display_id: string
  text: string
  type: string
}

const areApprovalsComplete = (data: AllApproversResponse): boolean => {
  const active = data?.workflows.some(x => x.approvers.some(a => a.is_active))
  const rejected = data?.workflows.some(x => x.approvers.some(a => a.is_rejected))
  return !rejected && !active
}

const workflowsHaveRejections = (data: AllApproversResponse): boolean => {
  return data?.workflows.some(x => x.approvers.some(a => a.is_rejected))
}

export const APPROVERS_ALL_KEY = 'approvers/all'
type AllApproversParams = { component_id?: number; api_module?: ApprovalModule }

export const useAllApproversQuery = ({
  component_id,
  api_module
}: AllApproversParams): UseQueryResult<AllApproversResponse> => {
  return useQuery({
    queryKey: [APPROVERS_ALL_KEY, { component_id, api_module }],
    queryFn: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const api = makeApproversApi(api_module!)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return api.workflow.all_approvers(component_id!).then((data: AllApproversResponse) => ({
        ...data,
        allApprovalsAreComplete: areApprovalsComplete(data),
        workflowsHaveRejections: workflowsHaveRejections(data)
      }))
    },
    enabled: Boolean(api_module && component_id)
  })
}

export const useMyApprovalsQuery = ({ component_id, api_module }: AllApproversParams) => {
  return useQuery({
    queryKey: [APPROVERS_ALL_KEY, { component_id, api_module }, 'my'],
    queryFn: (): Promise<TaskInstance | null> => {
      const api = makeApproversApi(api_module!)
      return api.workflow.approvers(component_id!).then(data => {
        const tasks = data?.results?.filter(t => t.can_approve && t.state === TaskState.Ready)
        return tasks?.length ? tasks[0] : null
      })
    },
    enabled: Boolean(api_module && component_id)
  })
}

// TODO (vic) - yech this feels like a lot of boiler plate, can this be better?
export const useAddApprovalCommentMutation = (
  { api_module, component_id }: AllApproversParams,
  opts?: UseMutationOptions<void, unknown, AddCommentParams>
) =>
  useMutation({
    mutationFn: ({ workflow_instance, comment }: AddCommentParams) => {
      return approverCommentsApi.addComment({ workflow_instance, comment })
    },
    ...opts,
    onSettled(...args) {
      queryClient.invalidateQueries([APPROVERS_ALL_KEY, { api_module, component_id }])
      if (typeof opts?.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useEditApprovalCommentMutation = (
  { api_module, component_id }: AllApproversParams,
  opts?: UseMutationOptions<void, unknown, EditCommentParams>
) =>
  useMutation({
    mutationFn: ({ id, comment }: EditCommentParams) => {
      return approverCommentsApi.editComment({ id, comment })
    },
    ...opts,
    onSettled(...args) {
      queryClient.invalidateQueries([APPROVERS_ALL_KEY, { api_module, component_id }])
      if (typeof opts?.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useDeleteApprovalCommentMutation = (
  { api_module, component_id }: AllApproversParams,
  opts?: UseMutationOptions<void, unknown, number>
) =>
  useMutation({
    mutationFn: (id: number) => {
      return approverCommentsApi.deleteComment(id)
    },
    ...opts,
    onSettled(...args) {
      queryClient.invalidateQueries([APPROVERS_ALL_KEY, { api_module, component_id }])
      if (typeof opts?.onSettled === 'function') opts.onSettled(...args)
    }
  })

type ApproveTaskVariables = { task_uuid: string; reasons: ApproveOptions }
export const useApproveTaskMutation = (
  { api_module, component_id }: AllApproversParams,
  opts?: UseMutationOptions<void, unknown, ApproveTaskVariables>
) =>
  useMutation({
    mutationFn: ({ task_uuid, reasons }) => {
      const api = makeApproversApi(api_module!)
      return api.workflow.approve(task_uuid, reasons)
    },
    ...opts,
    onSettled(...args) {
      queryClient.invalidateQueries([APPROVERS_ALL_KEY, { api_module, component_id }])
      if (typeof opts?.onSettled === 'function') opts.onSettled(...args)
    }
  })

type RejectTaskVariables = { task_uuid: string; reasons: ApproveOptions }
export const useRejectTaskMutation = (
  { api_module, component_id }: AllApproversParams,
  opts?: UseMutationOptions<void, unknown, RejectTaskVariables>
) =>
  useMutation({
    mutationFn: ({ task_uuid, reasons }) => {
      const api = makeApproversApi(api_module!)
      return api.workflow.deny(task_uuid, reasons.reason || '')
    },
    ...opts,
    onSettled(...args) {
      queryClient.invalidateQueries([APPROVERS_ALL_KEY, { api_module, component_id }])
      if (typeof opts?.onSettled === 'function') opts.onSettled(...args)
    }
  })

type NudgeTaskVariables = { task_uuid: string }
export const useNudgeTaskMutation = (
  { api_module }: Pick<AllApproversParams, 'api_module'>,
  opts?: UseMutationOptions<void, unknown, NudgeTaskVariables>
) =>
  useMutation({
    mutationFn: ({ task_uuid }) => {
      const api = makeApproversApi(api_module!)
      return api.workflow.renotify_approvers(task_uuid)
    },
    ...opts
  })
